import React, { useState } from "react";
import { MapPin, Phone, Mail, Clock, Send, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { COMPANY, saveContactMessage } from "../data/mock";
import { useRevealGroup } from "../hooks/useReveal";
import { PageHero, PillBadge, CTASection } from "../components/Shared";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";

const INFO = [
  { icon: MapPin, title: "Visit Us", lines: [COMPANY.address] },
  { icon: Phone, title: "Call Us", lines: [COMPANY.phone, COMPANY.phoneAlt] },
  { icon: Mail, title: "Email Us", lines: [COMPANY.email] },
  { icon: Clock, title: "Working Hours", lines: ["Open 7 Days a Week", "09:00 – 17:00"] },
];

export default function Contact() {
  const ref = useRevealGroup();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "", consent: false });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please agree to the data collection consent.");
      return;
    }
    setSubmitting(true);
    setTimeout(() => {
      saveContactMessage(form);
      setSubmitting(false);
      setForm({ name: "", email: "", phone: "", subject: "", message: "", consent: false });
      toast.success("Message sent!", { description: "Thank you for reaching out. We'll follow up shortly." });
    }, 700);
  };

  return (
    <div ref={ref}>
      <PageHero
        badge="Contact Us"
        title="We'd Love To Hear From You"
        subtitle="Ensuring you and your family are always protected in case of an emergency or unexpected event — free consultations, 7 days a week."
        image="https://images.pexels.com/photos/32206234/pexels-photo-32206234.jpeg"
      />

      {/* Info cards */}
      <section className="bg-[#F4F7FB] py-16">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 px-6 sm:grid-cols-2 lg:grid-cols-4">
          {INFO.map((c, i) => (
            <div key={c.title} data-reveal style={{ transitionDelay: `${i * 80}ms` }} className="group rounded-2xl bg-white p-8 text-center shadow-[0_2px_16px_rgba(0,111,185,0.07)] transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_20px_44px_rgba(0,111,185,0.13)]">
              <span className="mx-auto grid h-[52px] w-[52px] place-items-center rounded-2xl bg-[#E2F2FA] transition-all duration-500 group-hover:rotate-6 group-hover:bg-[#006FB9]">
                <c.icon className="h-6 w-6 text-[#006FB9] transition-colors duration-500 group-hover:text-[#B9DFF3]" />
              </span>
              <h3 className="mt-4 font-heading text-base font-bold text-[#0B2748]">{c.title}</h3>
              {c.lines.map((l) => (
                <p key={l} className="mt-1 text-sm text-[#526A7D]">{l}</p>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* Form + side panel */}
      <section className="bg-white py-20 lg:py-24">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-6 lg:grid-cols-[1.2fr_1fr]">
          <div data-reveal>
            <PillBadge>Request a Free Consultation</PillBadge>
            <h2 className="mt-4 font-heading text-3xl font-extrabold leading-[1.15] tracking-tight text-[#0B2748] sm:text-4xl">
              Send Us a <span className="text-[#0785C7]">Message</span>
            </h2>
            <p className="mt-4 text-[15.5px] leading-relaxed text-[#526A7D]">
              We'd love to help you make the very important decisions related to your insurance needs. Fill out the form and an advisor will get back to you promptly.
            </p>
            <form onSubmit={handleSubmit} className="mt-9 space-y-5">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <Input required placeholder="Full Name" value={form.name} onChange={(e) => set("name", e.target.value)} className="h-12 rounded-xl border-[#DCE9F1]" data-testid="contact-name" />
                <Input required type="email" placeholder="E-mail Address" value={form.email} onChange={(e) => set("email", e.target.value)} className="h-12 rounded-xl border-[#DCE9F1]" data-testid="contact-email" />
              </div>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <Input required type="tel" placeholder="Phone Number" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="h-12 rounded-xl border-[#DCE9F1]" data-testid="contact-phone" />
                <Input required placeholder="Subject" value={form.subject} onChange={(e) => set("subject", e.target.value)} className="h-12 rounded-xl border-[#DCE9F1]" data-testid="contact-subject" />
              </div>
              <Textarea required placeholder="Message" value={form.message} onChange={(e) => set("message", e.target.value)} className="min-h-[140px] rounded-xl border-[#DCE9F1]" data-testid="contact-message" />
              <label className="flex items-start gap-2.5 text-sm text-[#526A7D] cursor-pointer">
                <Checkbox checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} className="mt-0.5" data-testid="contact-consent" />
                <span>I agree that my submitted data is being collected and stored. For further details on handling user data, see our Privacy Policy.</span>
              </label>
              <button type="submit" disabled={submitting} className="btn-primary group" data-testid="contact-submit">
                {submitting ? "Sending…" : "Send Message"} <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </form>
          </div>

          <div className="space-y-6" data-reveal style={{ transitionDelay: "120ms" }}>
            <div className="overflow-hidden rounded-3xl">
              <img src="https://images.pexels.com/photos/8317705/pexels-photo-8317705.jpeg" alt="Family consultation" className="h-72 w-full object-cover transition-transform duration-700 hover:scale-105" />
            </div>
            <div className="rounded-3xl bg-[#006FB9] p-9 text-white">
              <h3 className="font-heading text-xl font-bold">Prefer to talk right away?</h3>
              <p className="mt-3 text-sm leading-relaxed text-[#C5DEEC]">
                Our insurance advisors are available for consultation 7 days a week. Don't hesitate to get the protection you need.
              </p>
              <a href={COMPANY.phoneHref} className="btn-light group mt-6 w-fit" data-testid="contact-call-now">
                <Phone className="h-4 w-4" /> {COMPANY.phone}
              </a>
            </div>
            <div className="rounded-3xl border border-[#E1ECF3] p-9">
              <h3 className="font-heading text-lg font-bold text-[#0B2748]">Serving Surrey & the Lower Mainland</h3>
              <p className="mt-3 text-sm leading-relaxed text-[#526A7D]">
                Based in Surrey, British Columbia — helping clients across BC in person, by phone, or online. In-person and virtual consultations available.
              </p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-[#006FB9]">
                <MapPin className="h-4 w-4" /> {COMPANY.address}
              </span>
            </div>
          </div>
        </div>
      </section>

      <CTASection title="Your Protection Starts With a Conversation" />
    </div>
  );
}
